import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useMemoizedFn } from 'ahooks';
import { Button, Drawer, Form } from 'antd';
import SlideRulerModule from '@/components/slide-ruler';
import { FieldName } from '../../enumData';
import cx from './index.module.scss';

interface Props {
  beforeMassage?: boolean;
  afterMassage?: boolean;
  lastMeasurement?: FieldName[];
  disabled?: boolean;
  bodyPartLabel: string;
  value: MeasurementResult;
  onChange?: (value: MeasurementResult) => void;
  filedNames: FieldName[];
  slideRulerBgImg?: string;
  slideRulerBgImgTwo?: string;
}

const checkObject = (obj: MeasurementResult, objArray: FieldName[]) => {
  if (obj === null) {
    return false;
  }
  if (Object?.keys(obj)?.length === 0) {
    return false; // 如果对象为空，返回false
  }

  for (let i = 0; i < objArray?.length; i++) {
    const name = objArray?.[i]?.name;

    if (!(name in obj) || obj?.[name] === null) {
      return false; // 如果对象中不存在数组中的名称值，返回false
    }
  }

  return true; // 如果每个属性都有值，返回true
};

const isAnyValuePresent = (objArray: FieldName[]) => {
  return objArray?.some(obj => {
    return obj?.value !== undefined && obj?.value !== null && obj?.value !== 0;
  });
};

const lastMeasurementFilter = (objArray: FieldName[], filedNames: FieldName[]) => {
  return filedNames?.map(item => ({
    ...item,
    value: objArray?.find(itm => itm.name === item?.name)?.value,
  }));
};

const InputValue: React.FC<Props> = ({
  beforeMassage = false,
  afterMassage = false,
  bodyPartLabel,
  value,
  onChange,
  filedNames,
  lastMeasurement,
}) => {
  const [isDrawerShow, setIsDrawerShow] = useState<boolean>(false);
  const [form] = Form.useForm();
  const currenFieldName = useRef<BodyPartMeasureName>(null);
  const handleCloseDrawer = useCallback(() => {
    setIsDrawerShow(false);
    onChange(form.getFieldsValue());
    setTimeout(() => {
      currenFieldName.current = null;
    }, 100);
  }, [form, onChange]);

  const handleInputValue = useCallback(() => {
    if (beforeMassage) {
      return;
    }
    currenFieldName.current = filedNames?.[0]?.name;

    setIsDrawerShow(true);
    form.setFieldsValue(value);
  }, [beforeMassage, filedNames, form, value]);

  const renderValue = useMemo(() => {
    // 填入了理疗前后值
    if (checkObject(value, filedNames)) {
      return (
        <div className={cx('input-measurement', { 'notAllow-click': beforeMassage })} onClick={handleInputValue}>
          <div className={cx('title')}>{afterMassage ? '理疗后' : '理疗前'}</div>
          <div className={cx('measurement-value-wrapper')}>
            {filedNames?.map((item, index) => (
              <div key={index} className={cx('measurement-value')}>
                <div className={cx('info')}>
                  <span>{item.label}</span>
                  <span className={cx('value', { 'after-value': afterMassage })}>{value?.[item.name]}</span>
                  <span>{item.unit}</span>
                </div>
                <div className={cx('line', { hide: filedNames?.length - 1 === index })} />
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      if (beforeMassage) {
        return isAnyValuePresent(lastMeasurementFilter(lastMeasurement, filedNames)) ? (
          <div className={cx('input-measurement')} onClick={handleInputValue}>
            <div className={cx('title')}>上次理疗</div>
            <div className={cx('measurement-value-wrapper')}>
              {filedNames?.map((item, index) => (
                <div key={index} className={cx('measurement-value')}>
                  <div className={cx('info')}>
                    <span>{item.label}</span>
                    <span className={cx('value', 'last-value')}>
                      {lastMeasurement?.find(last => last.name === item.name)?.value}
                    </span>
                    <span>{item.unit}</span>
                  </div>

                  <div className={cx('line', { hide: filedNames?.length - 1 === index })} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={cx('input-measurement')}>{'未输入理疗前' + bodyPartLabel}</div>
        );
      }
      return (
        <div className={cx('input-measurement')} onClick={handleInputValue}>
          <div>
            {afterMassage ? '请输入理疗后' : '请输入理疗前'}
            {bodyPartLabel}
          </div>
          <Icon name="arrow-right" className={cx('icon')} />
        </div>
      );
    }
  }, [afterMassage, beforeMassage, bodyPartLabel, filedNames, handleInputValue, lastMeasurement, value]);

  const handleFieldsChange = useMemoizedFn(
    useCallback((changedFields: any) => {
      currenFieldName.current = changedFields?.[0]?.name?.[0];
    }, [])
  );

  return (
    <>
      {renderValue}
      <Drawer
        className={cx('slide-ruler-drawer')}
        onClose={() => setIsDrawerShow(false)}
        maskClosable={false}
        width="520px"
        destroyOnClose={true}
        title={<span className={cx('drawer-title')}>{afterMassage ? '输入理疗后' : '输入理疗前' + bodyPartLabel}</span>}
        styles={{
          header: { background: '#E6E8EB' },
          body: { background: '#E6E8EB' },
        }}
        closeIcon={<Icon name="close-circle" className={cx('close-drawer')} color="#b3b4b6" />}
        open={isDrawerShow}
      >
        <div className={cx('content')}>
          <div className={cx('form', { 'extra-form': filedNames?.length > 1 })}>
            <Form form={form} clearOnDestroy onFieldsChange={handleFieldsChange}>
              <Form.Item shouldUpdate noStyle>
                {() => {
                  return (
                    <div className={cx('img-wrapper')}>
                      <img src={filedNames?.find(item => item?.name === currenFieldName.current)?.bodyPartImg} />
                      <div className={cx('value-desc', currenFieldName.current)}>
                        <div>
                          {filedNames?.find(item => item?.name === currenFieldName.current)?.label}
                          {filedNames?.length > 1 && '围'}
                        </div>
                        <div className={cx('current-value')}>
                          {form.getFieldsValue()?.[currenFieldName.current] ||
                            filedNames?.find(item => item.name === currenFieldName.current)?.defaultValue}
                        </div>
                        <div>{filedNames?.find(item => item?.name === currenFieldName.current)?.unit}</div>
                      </div>
                    </div>
                  );
                }}
              </Form.Item>

              {filedNames?.map(item => (
                <div key={item.name} className={cx('slide-ruler-module', { 'extra-box': filedNames?.length === 1 })}>
                  <Form.Item name={item.name} noStyle initialValue={item.defaultValue}>
                    <SlideRulerModule
                      label={item.label}
                      unit={item.unit}
                      defaultVal={item.defaultValue}
                      maxValue={item.maxValue}
                      minValue={item.minValue}
                    />
                  </Form.Item>
                </div>
              ))}
            </Form>
          </div>

          <div className={cx('note-text')}>为提升人体识别确切性，优化顾客体感，请准确选择上述身体信息。</div>

          <div className={cx('slide-ruler-footer')}>
            <Button color="primary" className={cx('confirm-btn')} onClick={handleCloseDrawer}>
              确定
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default InputValue;
